import React from 'react';
import { NodeHeader } from './components/NodeHeader';
import { NodeWrapper } from './components/NodeWrapper';
import { Body5, Container } from '@ui';
import OutputAggregationSourceHandle from './components/handles/OutputAggregationSourceHandle';
import OutputAggregationTargetHandle from './components/handles/OutputAggregationTargetHandle';
import styled from 'styled-components';
import { NodeDebugger } from './components/NodeDebugger';
import { FlatButton, TransparentFlatButton } from '@ui/buttons';
import { Separator } from '@ui';
import { PrettyJSON } from '@ui/text/PrettyJSON';

const ContentWrapper = styled.div`
  padding: 4px 8px;
  width: 420px;
`;
const AddConditionBtnWrapper = styled.div`
  padding: 0 0 16px 0;

  button {
    padding: 8px 16px;
  }
`;
const SettingStyling = styled.div`
  width: 100%;

  input,
  textarea {
    border: none;
    color: ${({ theme }) => theme.themeColors.text};
    background-color: ${({ theme }) => theme.themeColors.lowContrastBorder};
    border-radius: 4px;
    width: 100%;
    padding: 8px;
  }

  textarea {
    min-height: 68px;
    width: 100%;
  }

  ul {
    margin: 4px 0 0 0;
    padding: 0 36px 0 24px;

    li {
      &:first-child {
        padding-top: 0;
      }

      padding: 8px 0 0 0;
    }
  }

  select {
    //border: 1px solid ${({ theme }) => theme.themeColors.border};
    border: none;
    color: ${({ theme }) => theme.themeColors.text};
    background-color: ${({ theme }) => theme.themeColors.lowContrastBorder};
    border-radius: 4px;
    width: 100%;
    padding: 8px;
    -webkit-appearance: auto !important;
  }
`;
const LabelWrapper = styled.div`
  padding: 0 12px 0 0;
  min-width: 122px;
`;
const SettingWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;

  padding: 0 0 8px 0;
`;
const RemoveButtonWrapper = styled.div`
  cursor: pointer;
  padding: 0 3px 0 12px;

  svg {
    height: 18px;
    width: 18px;

    fill: ${({ theme }) => theme.themeColors.text};
  }
`;
const StepJsonDisplayWrapper = styled.div`
  margin: 8px auto 0 auto;
  opacity: 0.2;
  text-align: center;
`;

const MODES = {
  none: null,
  stepOutput: 'step',
  customOutput: 'customOutput',
};

const RemoveButton = ({ onClick }) => {
  return (
    <RemoveButtonWrapper onClick={onClick}>
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
        <path d="M9,3V4H4V6H5V19A2,2 0 0,0 7,21H17A2,2 0 0,0 19,19V6H20V4H15V3H9M7,6H17V19H7V6M9,8V17H11V8H9M13,8V17H15V8H13Z" />
      </svg>
    </RemoveButtonWrapper>
  );
};

export default React.memo((props) => {
  const { data, id, isConnectable } = props;
  const { stepOutputPriorityOrder } = data;

  const [showJson, setShowJson] = React.useState(false);
  const [mode, setMode] = React.useState(MODES.none);
  const [didAddCondition, setDidAddCondition] = React.useState(false);

  const onShowJsonClick = () => {
    setShowJson(!showJson);
  };

  React.useEffect(() => {
    const elementData = data.stepOutputPriorityOrder;
    console.log('elementData', elementData);
    if (elementData === null) {
      setMode(MODES.none);
      return;
    } else {
      if (typeof elementData === 'object') {
        setMode(MODES.customOutput);
        if ('condition' in data.stepOutputPriorityOrder) {
          setDidAddCondition(true);
        }
      }
      if (typeof elementData === 'string') {
        setMode(MODES.stepOutput);
      }
    }
  }, []);

  function handleCreateCustomOutputClick() {
    setMode(MODES.customOutput);

    const nextState = {
      stepOutputPriorityOrder: {},
    };
    nextState.stepOutputPriorityOrder.output_value = {
      step_id_output: '',
      output_element: '',
    };
    updateNodeData(nextState);
  }

  function handleCreateStepOutputClick() {
    setMode(MODES.stepOutput);

    const nextState = {
      stepOutputPriorityOrder: '',
    };
    updateNodeData(nextState);
  }

  function handleRemoveClick() {
    setMode(MODES.none);
  }

  function updateNodeData(d) {
    return data.actions.updateNodeData(id, d);
  }

  function handlePlanStepIdChange(e) {
    return updateNodeData({ stepOutputPriorityOrder: e.target.value });
  }

  function handleAddConditionClick() {
    setDidAddCondition(true);

    const nextState = {
      stepOutputPriorityOrder: { ...stepOutputPriorityOrder },
    };
    nextState.stepOutputPriorityOrder.condition = {
      step_id_output: '',
      output_element: '',
      contains: '',
    };
  }

  function handleRemoveConditionClick() {
    setDidAddCondition(false);

    const nextState = { stepOutputPriorityOrder: { ...stepOutputPriorityOrder } };
    delete nextState.stepOutputPriorityOrder.condition;
    updateNodeData(nextState);
  }

  function handleConditionStepIdOutputChange(e) {
    const value = e.target.value;
    const nextState = { stepOutputPriorityOrder: { ...stepOutputPriorityOrder } };
    if (!nextState.stepOutputPriorityOrder.condition) {
      nextState.stepOutputPriorityOrder.condition = {};
    }
    nextState.stepOutputPriorityOrder.condition.step_id_output = value;
    updateNodeData(nextState);
  }

  function handleConditionOutputElementChange(e) {
    const value = e.target.value;
    const nextState = { stepOutputPriorityOrder: { ...stepOutputPriorityOrder } };
    if (!nextState.stepOutputPriorityOrder.condition) {
      nextState.stepOutputPriorityOrder.condition = {};
    }
    nextState.stepOutputPriorityOrder.condition.output_element = value;
    updateNodeData(nextState);
  }

  function handleConditionContainsChange(e) {
    const value = e.target.value;
    const nextState = { stepOutputPriorityOrder: { ...stepOutputPriorityOrder } };
    if (!nextState.stepOutputPriorityOrder.condition) {
      nextState.stepOutputPriorityOrder.condition = {};
    }
    nextState.stepOutputPriorityOrder.condition.contains = value;
    updateNodeData(nextState);
  }

  function handleOutputValueStepIdOutputChange(e) {
    const value = e.target.value;
    const nextState = { stepOutputPriorityOrder: { ...stepOutputPriorityOrder } };
    if (!nextState.stepOutputPriorityOrder.output_value) {
      nextState.stepOutputPriorityOrder.output_value = {};
    }
    nextState.stepOutputPriorityOrder.output_value.step_id_output = value;
    updateNodeData(nextState);
  }

  function handleOutputValueOutputElementChange(e) {
    const value = e.target.value;
    const nextState = { stepOutputPriorityOrder: { ...stepOutputPriorityOrder } };
    if (!nextState.stepOutputPriorityOrder.output_value) {
      nextState.stepOutputPriorityOrder.output_value = {};
    }
    nextState.stepOutputPriorityOrder.output_value.output_element = value;
    updateNodeData(nextState);
  }

  const name = 'Output Aggregation';
  return (
    <NodeWrapper data-id={id} name={name} data={data} nodeData={props}>
      <NodeHeader id={id} name={name} Left={12} Right={12} actions={data.actions} />
      <ContentWrapper>
        <Container bottom={24}>
          {mode === MODES.customOutput ? (
            <Container row spaceBetween verticallyCentered top={8}>
              <Container>
                <Body5 bold>Custom Output Selected</Body5>
              </Container>
              <Container className="read-only-hide">
                <RemoveButton onClick={handleRemoveClick} />
              </Container>
            </Container>
          ) : (
            <Container row spaceBetween verticallyCentered top={8}>
              <Container>
                <Body5 bold>Plan Step Output Selected</Body5>
              </Container>
              <Container className="read-only-hide">
                <RemoveButton onClick={handleRemoveClick} />
              </Container>
            </Container>
          )}
          {mode === MODES.none ? (
            <Container top={8} className="read-only-hide">
              <Container>
                <FlatButton onClick={handleCreateCustomOutputClick}>Create Custom Output Rule</FlatButton>
              </Container>
              <Container top={8}>
                <FlatButton onClick={handleCreateStepOutputClick}>Create Step Output Rule</FlatButton>
              </Container>
            </Container>
          ) : mode === MODES.customOutput ? (
            <Container top={16}>
              <SettingStyling>
                {!didAddCondition ? (
                  <AddConditionBtnWrapper className="read-only-hide">
                    <FlatButton onClick={handleAddConditionClick}>Add Condition</FlatButton>
                  </AddConditionBtnWrapper>
                ) : (
                  <Container>
                    <Container row verticallyCentered spaceBetween>
                      <Body5 bold>Condition</Body5>
                      <Container className="read-only-hide">
                        <RemoveButton onClick={handleRemoveConditionClick} />
                      </Container>
                    </Container>
                    <Container top={16}>
                      <SettingWrapper>
                        <LabelWrapper>
                          <Body5>Step ID Output:</Body5>
                        </LabelWrapper>
                        <input
                          value={stepOutputPriorityOrder?.condition?.step_id_output || ''}
                          onChange={handleConditionStepIdOutputChange}
                        />
                      </SettingWrapper>
                    </Container>
                    <Container top={8}>
                      <SettingWrapper>
                        <LabelWrapper>
                          <Body5>Output Element:</Body5>
                        </LabelWrapper>
                        <input
                          value={stepOutputPriorityOrder?.condition?.output_element || ''}
                          onChange={handleConditionOutputElementChange}
                        />
                      </SettingWrapper>
                    </Container>
                    <Container top={8}>
                      <SettingWrapper>
                        <LabelWrapper>
                          <Body5>Contains:</Body5>
                        </LabelWrapper>
                        <input
                          value={stepOutputPriorityOrder?.condition?.contains || ''}
                          onChange={handleConditionContainsChange}
                        />
                      </SettingWrapper>
                    </Container>
                  </Container>
                )}

                <Container top={8}>
                  <Container>
                    <Body5 bold>Output Value</Body5>
                  </Container>
                  <Container top={16}>
                    <SettingWrapper>
                      <LabelWrapper>
                        <Body5>Step ID Output:</Body5>
                      </LabelWrapper>
                      <input
                        value={stepOutputPriorityOrder?.output_value?.step_id_output || ''}
                        onChange={handleOutputValueStepIdOutputChange}
                      />
                    </SettingWrapper>
                  </Container>
                  <Container top={8}>
                    <SettingWrapper>
                      <LabelWrapper>
                        <Body5>Output Element:</Body5>
                      </LabelWrapper>
                      <input
                        value={stepOutputPriorityOrder?.output_value?.output_element || ''}
                        onChange={handleOutputValueOutputElementChange}
                      />
                    </SettingWrapper>
                  </Container>
                </Container>
              </SettingStyling>
            </Container>
          ) : mode === MODES.stepOutput ? (
            <SettingStyling>
              <Container top={8}>
                <SettingWrapper>
                  <LabelWrapper>
                    <Body5>Plan Step ID:</Body5>
                  </LabelWrapper>
                  <input value={stepOutputPriorityOrder} onChange={handlePlanStepIdChange} />
                </SettingWrapper>
              </Container>
            </SettingStyling>
          ) : null}
        </Container>

        {showJson ? (
          <Container top={24} style={{ wordBreak: 'break-word' }}>
            <Separator />
            <Container top={16}>
              <PrettyJSON value={data.stepOutputPriorityOrder} />
            </Container>
            <Container top={8} centerAll>
              <TransparentFlatButton onClick={onShowJsonClick}>Hide JSON</TransparentFlatButton>
            </Container>
          </Container>
        ) : (
          <StepJsonDisplayWrapper>
            <TransparentFlatButton onClick={onShowJsonClick}>Show JSON</TransparentFlatButton>
          </StepJsonDisplayWrapper>
        )}
      </ContentWrapper>

      <OutputAggregationTargetHandle {...props} isConnectable={isConnectable} />
      <OutputAggregationSourceHandle {...props} isConnectable={isConnectable} />

      <NodeDebugger id={id} />
    </NodeWrapper>
  );
});
